import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import AuthLayout from '../components/AuthLayout';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.forgotPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={t('auth.forgotPasswordTitle')} subtitle={t('auth.forgotPasswordDescription')}>
      {success ? (
        <div className="text-center">
          <div
            className="mb-4 p-3 rounded-md text-sm"
            style={{
              background: 'var(--gp-success-soft)',
              border: '1px solid var(--gp-success)',
              color: 'var(--gp-success)',
            }}
            role="status"
          >
            {t('auth.forgotPasswordSuccess')}
          </div>
          <p className="text-sm gp-text-muted mb-5">{t('auth.forgotPasswordInstructions')}</p>
          <Link to="/login" className="gp-back-link">
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            <span>{t('auth.backToLogin')}</span>
          </Link>
        </div>
      ) : (
        <>
          {error && (
            <div className="gp-alert-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
                {t('auth.email')}
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted"
                  aria-hidden="true"
                />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('auth.emailPlaceholder')}
                  className="input-field pl-10"
                  autoComplete="email"
                />
              </div>
            </div>

            <button type="submit" className="w-full gp-btn-primary py-3" disabled={loading}>
              {loading ? (
                <>
                  <span className="gp-spinner !w-4 !h-4 !mx-0 border" aria-hidden="true" />
                  <span>{t('auth.sending')}</span>
                </>
              ) : (
                <span>{t('auth.sendResetLink')}</span>
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <Link to="/login" className="gp-back-link text-sm">
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
              <span>{t('auth.backToLogin')}</span>
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
